import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import Drawer from '@material-ui/core/Drawer';
import useCenterlineAiStyles from './styles';
import { activeChat, showsWelcome } from './core/chat-reducer';
import { useCenterlineAi } from './CenterlineAiProvider';
import ChatSidebar from './ChatSidebar';
import Thread from './Thread';
import Composer from './Composer';
import { ChatGlyph, PlusGlyph } from './Glyphs';

/**
 * The chats and the conversation. Wide, the chats are the sidebar beside the conversation. Narrow
 * (CenterlineAiPage: below the theme's md breakpoint), the conversation takes the whole width and
 * the chats open over it from a bar at its top, which also names the open chat and starts a new
 * one; opening or starting a chat there closes them again.
 */

function ConversationBar({ chatsOpen, onOpenChats }: { chatsOpen: boolean; onOpenChats(): void }) {
  const classes = useCenterlineAiStyles();
  const { state, actions } = useCenterlineAi();
  // An untitled chat shows no title; the empty span still keeps "+" at the right edge.
  const title = activeChat(state)?.title || '';

  return (
    <div className={classes.conversationBar}>
      <button
        type="button"
        className={classes.barButton}
        aria-haspopup="dialog"
        aria-expanded={chatsOpen}
        onClick={onOpenChats}
      >
        <ChatGlyph />
        Chats
      </button>
      <span className={classes.barTitle} title={title || undefined}>
        {title}
      </span>
      <button
        type="button"
        className={classes.barIconButton}
        aria-label="New chat"
        title="New chat"
        onClick={actions.newChat}
      >
        <PlusGlyph />
      </button>
    </div>
  );
}

export default function ChatsAndConversation({ narrow }: { narrow: boolean }) {
  const classes = useCenterlineAiStyles();
  const { state } = useCenterlineAi();
  const [chatsOpen, setChatsOpen] = useState(false);
  const closeChats = () => setChatsOpen(false);

  // Widening the window puts the chats back beside the conversation; narrowing it again must not
  // reopen them over it.
  useEffect(() => {
    if (!narrow) setChatsOpen(false);
  }, [narrow]);

  return (
    <>
      {narrow ? (
        <Drawer
          anchor="left"
          open={chatsOpen}
          onClose={closeChats}
          classes={{ paper: classes.chatsDrawerPaper }}
          // MUI's modal traps focus and returns it to the Chats button; say what it is.
          PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-label': 'Chats' }}
        >
          <ChatSidebar onClose={closeChats} />
        </Drawer>
      ) : (
        <ChatSidebar />
      )}
      <section
        className={clsx(
          classes.card,
          classes.conversation,
          showsWelcome(state) && classes.conversationWelcome,
          narrow && classes.conversationNarrow,
        )}
        aria-label="Conversation"
      >
        {narrow ? (
          <ConversationBar chatsOpen={chatsOpen} onOpenChats={() => setChatsOpen(true)} />
        ) : null}
        <Thread />
        <Composer />
      </section>
    </>
  );
}
